import { useState, useEffect } from "react";
import axios from "axios";
import classes from "./Insights.module.css";
import CommonCard from "../../components/ui/CommonCard/CommonCard";
import CustomBarChart from "../../components/Charts/CustomBarChart";
import CustomTinyAreaChart from "../../components/Charts/CustomTinyAreaChart";
import Table from "../../components/Table/Table";
import { IoStar } from "react-icons/io5";
import { env } from "../../constants/env";

const Insights = () => {
  const [selectedYear, setSelectedYear] = useState("2025");

  // Live state data
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [customerStats, setCustomerStats] = useState(null);
  const [topCars, setTopCars] = useState([]);
  const [categoryPerformance, setCategoryPerformance] = useState([]);

  // Loading states
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [loadingTopCars, setLoadingTopCars] = useState(false);
  const [loadingCategoryPerf, setLoadingCategoryPerf] = useState(false);

  const [availableYears, setAvailableYears] = useState([]);

  // Error states (optional)
  const [error, setError] = useState(null);

  // Columns for table
  const columns = [
    // { key: "id", label: "ID" },
    { key: "car", label: "CAR" },
    { key: "bookings", label: "BOOKINGS" },
    { key: "revenue", label: "REVENUE" },
    { key: "rating", label: "RATING" },
  ];

  // Fetch revenue trend for selected year
  useEffect(() => {
    const fetchRevenueTrend = async () => {
      setLoadingRevenue(true);
      try {
        const res = await axios.get(
          `${env.apiUrl}/api/insights/revenue-trend/${selectedYear}`
        );
        setRevenueTrend(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch revenue trend");
      } finally {
        setLoadingRevenue(false);
      }
    };
    fetchRevenueTrend();
  }, [selectedYear]);

  // Fetch customer metrics once on mount
  useEffect(() => {
    const fetchCustomerMetrics = async () => {
      setLoadingCustomer(true);
      try {
        const res = await axios.get(
          `${env.apiUrl}/api/insights/customer-metrics`
        );
        setCustomerStats(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch customer metrics");
      } finally {
        setLoadingCustomer(false);
      }
    };
    fetchCustomerMetrics();
  }, []);

  // Fetch top performing cars once on mount
  useEffect(() => {
    const fetchTopCars = async () => {
      setLoadingTopCars(true);
      try {
        const res = await axios.get(
          `${env.apiUrl}/api/insights/top-performing-cars`
        );
        setTopCars(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch top cars");
      } finally {
        setLoadingTopCars(false);
      }
    };
    fetchTopCars();
  }, []);

  // Fetch performance by category once on mount
  useEffect(() => {
    const fetchCategoryPerformance = async () => {
      setLoadingCategoryPerf(true);
      try {
        const res = await axios.get(
          `${env.apiUrl}/api/insights/performance-by-category`
        );
        setCategoryPerformance(res.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch performance by category");
      } finally {
        setLoadingCategoryPerf(false);
      }
    };
    fetchCategoryPerformance();
  }, []);

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await axios.get(
          `${env.apiUrl}/api/insights/available-years`
        );
        setAvailableYears(response.data);
        setSelectedYear(response.data[0] || ""); // default to first year
      } catch (error) {
        console.error("Error fetching years:", error);
      }
    };

    fetchYears();
  }, []);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Prepare customer stats array with fallback to loading state
  const customerStatsData = customerStats
    ? [
        {
          name: "Total bookings this year",
          value: customerStats.totalBookings.toLocaleString(),
        },
        {
          name: "Avg booking value",
          value: `K${customerStats.avgBookingValue}`,
        },
        { name: "Total revenue", value: `K${customerStats.totalRevenue}` },
        { name: "Avg rating", value: customerStats.avgRating },
      ]
    : [];

  return (
    <div className={classes.wrapper}>
      <div className="row mb-4">
        <div className="col-md-8">
          <CommonCard className={`pe-3`}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className={classes.cardRevenueHeader}>
                <h5>Revenue trend</h5>
              </div>
              <div className={classes.yearSelector}>
                <select
                  className="form-select"
                  value={selectedYear}
                  onChange={handleYearChange}
                  disabled={availableYears.length === 0}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {loadingRevenue ? (
              <p>Loading revenue trend...</p>
            ) : (
              <CustomTinyAreaChart
                selectedYear={selectedYear}
                data={revenueTrend}
              />
            )}
          </CommonCard>
        </div>
        <div className="col-md-4">
          <CommonCard className={classes.customerCard}>
            <h3>Customer metrics</h3>
            {loadingCustomer ? (
              <p>Loading customer metrics...</p>
            ) : (
              <div className={`d-flex flex-column ${classes.customerMetrics}`}>
                {customerStatsData.map((item, i) => (
                  <div
                    className="d-flex align-items-center justify-content-between"
                    key={i}
                  >
                    <h4>{item.name}</h4>
                    <div className="d-flex align-items-center gap-1">
                      {item.name === "Avg rating" && (
                        <IoStar color="var(--primary-color)" />
                      )}
                      <p>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CommonCard>
        </div>
      </div>

      <h4 className="mt-4 mb-3">Top performing cars</h4>
      <CommonCard className={`mb-4`}>
        {loadingTopCars ? (
          <p>Loading top performing cars...</p>
        ) : (
          <Table
            dataType={"Performing-cars"}
            data={topCars}
            columns={columns}
          />
        )}
      </CommonCard>

      <CommonCard>
        <div className={classes.cardHeader}>
          <h5>Performance by car category</h5>
        </div>
        {loadingCategoryPerf ? (
          <p>Loading category performance...</p>
        ) : (
          <CustomBarChart data={categoryPerformance} />
        )}
      </CommonCard>
    </div>
  );
};

export default Insights;
